import { useEffect } from 'react';

import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import {
    fetchGetApplication,
    fetchGetFormalOffers
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import ApprovalExpiredView from './approvalExpiredView/ApprovalExpiredView';
import MainContentPending from '../pageStructure/mainContentPending';
import { ApprovedView as OldApprovedView } from './approvedView/ApprovedView';
import DeclinedView from './declinedView/DeclinedView';
import DefaultView from './defaultView/DefaultView';
import DeferredView from './deferredView/DeferredView';
import { getFormalOfferIds } from './getFormalOfferIds';
import { getOfferStatus, TOfferStatus } from './getOfferStatus';
import { ApprovedView } from './offersPage/ApprovedView/ApprovedView';
import { ApplicationPendingView } from './pendingView/applicationPendingView/ApplicationPendingView';
import RevokedView from './revokedView/RevokedView';
import { useStateKey } from '../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../tracking/pageView';
import FallbackErrorModal from '../modals/fallbackErrorModal/FallbackErrorModal';
import { AwaitingFundsView } from '../multiLoanDashboard/awaitingFundsView/AwaitingFundsView';

const MyOffers = () => {
    useTrackPageView({ name: 'My Offers' });

    const stateKey = useStateKey();

    const {
        isLoading: getFormalOffersResponseLoading,
        isError: getFormalOffersResponseError,
        data: getFormalOffersResponse
    } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey })
    });

    const {
        isLoading: getApplicationResponseLoading,
        isError: getApplicationResponseError,
        data: getApplicationResponse
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        select: (data) => {
            // Ignore iwocapay applications - they're handled by the iwocapay dashboard
            if (
                data?.data?.requested_products?.length === 1 &&
                data?.data?.requested_products?.[0]?.product_type === 'iwocapay'
            ) {
                return { ...data, data: null };
            }
            return data;
        }
    });

    const {
        isLoading: getAccountsResponseLoading,
        isError: getAccountsResponseError,
        data: getAccountsResponse
    } = useQuery({
        ...getAccountsQueryConfig(stateKey)
    });

    const loading =
        getApplicationResponseLoading ||
        getFormalOffersResponseLoading ||
        getAccountsResponseLoading;
    const error =
        getApplicationResponseError ||
        getFormalOffersResponseError ||
        getAccountsResponseError;

    let offerStatus: TOfferStatus | null = null;

    useEffect(() => {
        //amplitude tracking
        if (
            getFormalOffersResponse &&
            getApplicationResponse &&
            getApplicationResponse.data &&
            getFormalOffersResponse.data &&
            offerStatus
        ) {
            const formalOfferIds = getFormalOfferIds(getFormalOffersResponse);

            tracking.ampli.myOffersVisited({
                formal_offer_ids: formalOfferIds,
                application_status: getApplicationResponse!.data!.status,
                page_status: offerStatus
            });
        }
    }, [getApplicationResponse, getFormalOffersResponse, offerStatus]);

    if (loading) {
        return <MainContentPending data-testid="my-offers-loading" />;
    }
    if (error) {
        return <FallbackErrorModal />;
    }

    offerStatus = getOfferStatus(
        getFormalOffersResponse!,
        getApplicationResponse!,
        getAccountsResponse!
    );

    switch (offerStatus) {
        case 'application_declined':
            return <DeclinedView />;
        case 'application_pending':
            return <ApplicationPendingView />;
        case 'application_deferred':
            return <DeferredView />;
        case 'offer_expired':
            return <ApprovalExpiredView />;
        case 'application_offer_revoked':
            return getCountry() === 'DE' ? <RevokedView /> : <DefaultView />;
        case 'offered':
            return getCountry() === 'DE' ? (
                <OldApprovedView />
            ) : (
                <ApprovedView />
            );
        case 'no_offer':
            return <DefaultView />;
        case 'awaiting_funds':
            return <AwaitingFundsView />;
        default:
            return <DefaultView />;
    }
};

// eslint-disable-next-line import/no-default-export
export default MyOffers;
